import { Box, Flex, Text, Tooltip } from '@chakra-ui/react'
import { ReactNode, useMemo, useRef, useState } from 'react'
import { AiOutlineSound, AiFillFile, AiOutlineStock } from 'react-icons/ai'
import { BiPackage } from 'react-icons/bi'
import {
  BsCheck2Circle,
  BsCheckCircleFill,
  BsExclamationTriangle
} from 'react-icons/bs'
import {
  FaBoxOpen,
  FaSortAmountDownAlt,
  FaCogs,
  FaBalanceScale,
  FaShieldAlt,
  FaBan
} from 'react-icons/fa'
import { IoIosCloseCircle, IoMdCloseCircleOutline } from 'react-icons/io'
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md'
import { RiSecurePaymentLine } from 'react-icons/ri'

import useMrpPurchase from '@hook/mrp/use-purchase'

import { ReactComponent as IncrementStock } from '../../../Icons/increment-stock1.svg'
import { ReactComponent as StockMinor } from '../../../Icons/stockMinor1.svg'

import '../../Layout/TableGrid/Header/styles.css'

interface HeaderColumn {
  name: string
  id: string
  show?: boolean
  isAlwaysVisible?: boolean
  isNumeric?: boolean
}

interface HeaderGroup {
  group: string
  id?: string
  columns?: HeaderColumn[]
  align?: string
}

interface Props {
  data: any[]
  query: string
  height: string
  headers: HeaderGroup[]
}

interface SupplierGroup {
  fornecedorId: number | string
  fornecedorNome: string
  items: any[]
  totalCusto: number
  totalRecomCusto: number
  skuCount: number
}

// Campos considerados na busca (evita serializar a linha inteira a cada filtro)
const SEARCH_FIELDS = [
  'produto-id',
  'produto-material',
  'produto-categoria',
  'produto-categoria-compacta',
  'produto-sub-categoria',
  'produto-mundo',
  'centro-id',
  'centro-nome',
  'fornecedor-id',
  'fornecedor-nome',
  'gerente-produto-desc'
]

function parseNumericPtBR(val: string | number): number {
  if (typeof val === 'number') return val
  if (!val) return 0
  return parseFloat(String(val).replace(/\./g, '').replace(',', '.')) || 0
}

// eslint-disable-next-line no-undef
const PurchaseGroupedView: React.FC<Props> = ({
  data = [],
  query,
  height,
  headers = []
}) => {
  const [expanded, setExpanded] = useState<Set<string | number>>(new Set())
  const { handleUpdateWanted, alteredSkuIds } = useMrpPurchase()
  // Último valor enviado por SKU: evita reenviar no blur que segue o Enter
  const lastSentRef = useRef(new Map<number, string>())

  const sendWanted = (item: any, text: string) => {
    const typed = text.trim()
    const current = String(item['compra-desejada'] ?? '').replace(/^[TF]/, '')
    // Blur sem mudança não dispara PUT
    if (
      typed === current ||
      lastSentRef.current.get(item['estoque-id']) === typed
    ) {
      return
    }
    lastSentRef.current.set(item['estoque-id'], typed)
    handleUpdateWanted(item['estoque-id'], typed)
  }

  // Compute visible groups/columns — same logic as TableGrid
  const visibleGroups = useMemo(() => {
    const arr: HeaderGroup[] = []
    for (const row of headers ?? []) {
      if (row.id) {
        arr.push(row)
        continue
      }
      const available = (row.columns || []).filter(
        c => c.show === true || (c as any).isAlwaysVisible === true
      )
      if (available.length === 0) continue
      arr.push({ ...row, columns: available })
    }
    return arr
  }, [headers])

  const allColumns = useMemo(() => {
    const cols: HeaderColumn[] = []
    for (const grp of visibleGroups) {
      if (grp.columns) cols.push(...grp.columns)
    }
    return cols
  }, [visibleGroups])

  const filtered = useMemo(() => {
    const safeData = data ?? []
    if (!query) return safeData
    const q = query.toLowerCase()
    return safeData.filter(item =>
      SEARCH_FIELDS.some(field =>
        String(item[field] ?? '')
          .toLowerCase()
          .includes(q)
      )
    )
  }, [data, query])

  const groups = useMemo((): SupplierGroup[] => {
    const map = new Map<string | number, SupplierGroup>()
    for (const item of filtered) {
      const key = item['fornecedor-id'] ?? 0
      if (!map.has(key)) {
        map.set(key, {
          fornecedorId: key,
          fornecedorNome: item['fornecedor-nome'] || `Fornecedor ${key}`,
          items: [],
          totalCusto: 0,
          totalRecomCusto: 0,
          skuCount: 0
        })
      }
      const grp = map.get(key)!
      grp.items.push(item)
      grp.totalCusto += parseNumericPtBR(item['compra-custo'])
      grp.totalRecomCusto += parseNumericPtBR(item['recomendacao-custo'])
      grp.skuCount += 1
    }
    return Array.from(map.values()).sort((a, b) =>
      String(a.fornecedorNome).localeCompare(String(b.fornecedorNome))
    )
  }, [filtered])

  const fmt = (val: number) =>
    val.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

  const renderAlertIcon = (value: any): ReactNode => {
    const status = parseInt(value, 10)
    const iconMap: Record<number, { label: string; icon: ReactNode }> = {
      0: { label: 'Ideal', icon: <BsCheck2Circle title="Ideal" /> },
      1: { label: 'Pacote', icon: <FaBoxOpen title="Pacote" /> },
      2: {
        label: 'Estoque Máximo',
        icon: <AiOutlineStock title="Estoque Máximo" />
      },
      3: {
        label: 'Compra Mínima',
        icon: <FaSortAmountDownAlt title="Compra Mínima" />
      },
      4: { label: 'Opção de Compra', icon: <FaCogs title="Opção de Compra" /> },
      5: {
        label: 'Sem Estoque',
        icon: <IoIosCloseCircle title="Sem Estoque" />
      },
      6: {
        label: 'Indisponível',
        icon: <IoMdCloseCircleOutline title="Indisponível" />
      },
      7: {
        label: 'Compra Mínima > Estoque Máximo',
        icon: <FaBalanceScale title="Compra Mínima > Estoque Máximo" />
      },
      8: {
        label: 'Cobertura < Estoque Segurança',
        icon: <FaShieldAlt title="Cobertura < Estoque Segurança" />
      },
      9: {
        label: 'Compra Desnecessária',
        icon: <FaBan title="Compra Desnecessária" />
      },
      10: {
        label: 'Estoque Arbitrado',
        icon: <StockMinor title="Estoque Arbitrado" />
      },
      11: {
        label: 'Lote Múltiplo',
        icon: <IncrementStock title="Lote Múltiplo" />
      },
      12: {
        label: 'Produto Similar',
        icon: <IncrementStock title="Produto Similar" />
      },
      13: { label: 'Produto Novo', icon: <AiFillFile title="Produto Novo" /> },
      14: { label: 'Analisar', icon: <AiOutlineSound title="Analisar" /> },
      15: { label: 'OK', icon: <BsCheckCircleFill title="OK" /> },
      16: {
        label: 'Estoque Baixo CD',
        icon: <StockMinor title="Estoque Baixo CD" />
      },
      17: {
        label: 'Estoque Mínimo',
        icon: <StockMinor title="Estoque Mínimo" />
      },
      18: {
        label: 'Incremento Estoque',
        icon: <IncrementStock title="Incremento Estoque" />
      }
    }
    const entry = iconMap[status]
    if (!entry) return null
    return (
      <Tooltip label={entry.label} placement="top" fontSize="xs" hasArrow>
        <span>{entry.icon}</span>
      </Tooltip>
    )
  }

  const toggle = (id: string | number) => {
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  return (
    <Box h={height} overflowY="auto" px={2} py={1}>
      {groups.map(grp => {
        const isOpen = expanded.has(grp.fornecedorId)
        return (
          <Box
            key={grp.fornecedorId}
            mb={2}
            borderRadius="8px"
            border="1px solid"
            borderColor="gray.200"
            overflow="hidden"
            boxShadow="sm"
          >
            {/* ── Supplier card header ── */}
            <Flex
              align="center"
              px={4}
              py={3}
              bg="white"
              cursor="pointer"
              onClick={() => toggle(grp.fornecedorId)}
              _hover={{ bg: 'gray.50' }}
              borderLeft="4px solid"
              borderLeftColor="green.500"
              gap={4}
            >
              <Box color="gray.500" flexShrink={0}>
                {isOpen ? (
                  <MdKeyboardArrowDown size={20} />
                ) : (
                  <MdKeyboardArrowRight size={20} />
                )}
              </Box>

              <Box flex={1} minW={0}>
                <Text
                  fontWeight="700"
                  fontSize="13px"
                  color="gray.800"
                  textTransform="uppercase"
                  letterSpacing="0.3px"
                  isTruncated
                >
                  {grp.fornecedorNome}
                </Text>
                <Text fontSize="11px" color="gray.400">
                  Cód. {grp.fornecedorId}
                </Text>
              </Box>

              <Flex gap={3} align="center" flexShrink={0}>
                {/* SKUs */}
                <Flex
                  direction="column"
                  align="center"
                  bg="blue.50"
                  borderRadius="8px"
                  px={3}
                  py="6px"
                  minW="68px"
                >
                  <Flex align="center" gap={1} color="blue.500" mb="2px">
                    <BiPackage size={12} />
                    <Text
                      fontSize="10px"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="0.4px"
                    >
                      SKUs
                    </Text>
                  </Flex>
                  <Text
                    fontSize="20px"
                    fontWeight="800"
                    color="blue.700"
                    lineHeight="1"
                  >
                    {grp.skuCount}
                  </Text>
                </Flex>

                {/* Custo recomendado */}
                <Flex
                  direction="column"
                  align="center"
                  bg="orange.50"
                  borderRadius="8px"
                  px={3}
                  py="6px"
                  minW="140px"
                >
                  <Flex align="center" gap={1} color="orange.500" mb="2px">
                    <RiSecurePaymentLine size={12} />
                    <Text
                      fontSize="10px"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="0.4px"
                    >
                      Custo Recomend.
                    </Text>
                  </Flex>
                  <Text
                    fontSize="14px"
                    fontWeight="700"
                    color="orange.700"
                    lineHeight="1"
                  >
                    R$ {fmt(grp.totalRecomCusto)}
                  </Text>
                </Flex>

                {/* Custo desejado */}
                <Flex
                  direction="column"
                  align="center"
                  bg="green.50"
                  borderRadius="8px"
                  px={3}
                  py="6px"
                  minW="140px"
                >
                  <Flex align="center" gap={1} color="green.600" mb="2px">
                    <RiSecurePaymentLine size={12} />
                    <Text
                      fontSize="10px"
                      fontWeight="600"
                      textTransform="uppercase"
                      letterSpacing="0.4px"
                    >
                      Custo Desejado
                    </Text>
                  </Flex>
                  <Text
                    fontSize="14px"
                    fontWeight="700"
                    color="green.700"
                    lineHeight="1"
                  >
                    R$ {fmt(grp.totalCusto)}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            {/* ── Products table ── */}
            {isOpen && (
              <Box
                overflowX="auto"
                borderTop="1px solid"
                borderColor="gray.100"
              >
                <table className="scl-table-grid" style={{ width: '100%' }}>
                  <thead
                    style={{
                      background: '#EDF2F7',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1
                    }}
                  >
                    {/* Row 1 — group labels */}
                    <tr className="actions">
                      {visibleGroups.map(g => {
                        if (g.id) {
                          return (
                            <td
                              key={g.id + '+header'}
                              rowSpan={2}
                              align={(g as any).align || 'left'}
                            >
                              {g.group}
                            </td>
                          )
                        }
                        return (
                          <th
                            key={`${g.group
                              .split(' ')
                              .join('-')
                              .toLowerCase()}-hdr`}
                            colSpan={g.columns!.length}
                            scope="colgroup"
                          >
                            {g.group}
                          </th>
                        )
                      })}
                    </tr>
                    {/* Row 2 — column names */}
                    <tr className="hearder__th">
                      {visibleGroups.map(g => {
                        if (g.id || !g.columns) return null
                        return g.columns.map(col => (
                          <th
                            key={`${col.id}-hdr`}
                            className="hearder__th"
                            align={(g as any).align || 'left'}
                            scope="col"
                          >
                            <Box className="hearder__text-container">
                              <span className="hearder__text">{col.name}</span>
                            </Box>
                          </th>
                        ))
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {grp.items.map(item => {
                      const isAltered = alteredSkuIds.has(item['estoque-id'])
                      const isDisabled = !!item.disabled
                      return (
                        <tr key={item['estoque-id']}>
                          {allColumns.map(col => {
                            if (col.id === 'compra-desejada') {
                              const value = item[col.id] ?? ''
                              return (
                                <td
                                  key={col.id}
                                  align="left"
                                  className="custom td-element"
                                  style={{
                                    padding: '8px',
                                    background: isAltered
                                      ? '#ffeef3'
                                      : '#FEFFC7',
                                    cursor: isDisabled ? 'default' : 'text'
                                  }}
                                >
                                  <Flex
                                    width="100%"
                                    minHeight="24px"
                                    alignItems="center"
                                    onClick={e => {
                                      if (isDisabled) return
                                      const editableBox =
                                        e.currentTarget.querySelector(
                                          '[contenteditable="true"]'
                                        ) as HTMLElement
                                      if (editableBox) {
                                        editableBox.focus()
                                        const range = document.createRange()
                                        range.selectNodeContents(editableBox)
                                        const selection = window.getSelection()
                                        selection?.removeAllRanges()
                                        selection?.addRange(range)
                                      }
                                    }}
                                    cursor={isDisabled ? 'default' : 'text'}
                                  >
                                    {String(value).substring(0, 1) === 'T' ? (
                                      <BsExclamationTriangle
                                        color="red"
                                        style={{
                                          marginRight: '4px',
                                          flexShrink: 0
                                        }}
                                      />
                                    ) : null}
                                    <Box
                                      contentEditable={!isDisabled}
                                      onFocus={() => {
                                        lastSentRef.current.delete(
                                          item['estoque-id']
                                        )
                                      }}
                                      onBlurCapture={e => {
                                        if (isDisabled) return
                                        sendWanted(
                                          item,
                                          e.currentTarget.textContent || ''
                                        )
                                      }}
                                      onKeyDown={e => {
                                        if (isDisabled) {
                                          e.preventDefault()
                                          return
                                        }
                                        if (
                                          e.keyCode === 46 ||
                                          e.keyCode === 8 ||
                                          (e.keyCode > 95 && e.keyCode < 106) ||
                                          e.keyCode === 37 ||
                                          e.keyCode === 39
                                        ) {
                                          // allow
                                        } else if (
                                          e.keyCode < 48 ||
                                          e.keyCode > 57
                                        ) {
                                          e.preventDefault()
                                        }
                                        if (e.key === '-') {
                                          e.preventDefault()
                                        }
                                        if (e.key === 'Enter') {
                                          e.preventDefault()
                                          sendWanted(
                                            item,
                                            e.currentTarget.textContent || ''
                                          )
                                          e.currentTarget.blur()
                                        }
                                      }}
                                      width="100%"
                                      minHeight="20px"
                                      outline="none"
                                      _focus={{
                                        outline: '2px solid #3182ce',
                                        borderRadius: '2px'
                                      }}
                                      suppressContentEditableWarning
                                    >
                                      {String(value).replace(/^[TF]/, '')}
                                    </Box>
                                  </Flex>
                                </td>
                              )
                            }
                            if (col.id === 'recomendacao-tipo-alerta') {
                              return (
                                <td
                                  key={col.id}
                                  align="center"
                                  className="td-element"
                                >
                                  {renderAlertIcon(item[col.id])}
                                </td>
                              )
                            }
                            return (
                              <td
                                key={col.id}
                                align={col.isNumeric ? 'right' : 'left'}
                                className="td-element"
                              >
                                {item[col.id] ?? '—'}
                              </td>
                            )
                          })}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Box>
            )}
          </Box>
        )
      })}

      {groups.length === 0 && (
        <Flex justify="center" align="center" h="120px">
          <Text color="gray.400" fontSize="13px">
            Nenhum fornecedor encontrado
          </Text>
        </Flex>
      )}
    </Box>
  )
}

export default PurchaseGroupedView
